import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import {
  MessageCircle, Package, MapPin, Loader2, X, Plus, Minus,
  ShoppingCart, Trash2, ChevronRight, Eye, Search, Gift, LayoutGrid, List
} from 'lucide-react';
import toast from 'react-hot-toast';
import LazyLoad from 'react-lazyload';

const StoreView = () => {
  const { shopName } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');

  const [cart, setCart] = useState([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // --- API FETCH LOGIC START ---
  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        const res = await api.get(`/products/shop/${shopName}`);
        const shopData = res.data.shop;
        const productData = res.data.products;

        setProducts(productData);
        setSeller(shopData);

        // FIX: Category object ya string dono ko handle karein
        const storeDeals = productData.filter(p => (p.category?.name || p.category) === 'Deals');
        setDeals(storeDeals);

        if (shopData) {
          document.title = `${shopData.shopName.toUpperCase()} | SocialCart`;
          if (shopData.shopLogo) {
            const link = document.querySelector("link[rel~='icon']");
            if (link) link.href = shopData.shopLogo;
          }
        }
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchStoreData();
    return () => {
      document.title = "SocialCart | Your WhatsApp Store";
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = "/favicon.ico";
    };
  }, [shopName]);
  // --- API FETCH LOGIC END ---

  // --- UPDATED FILTERING LOGIC ---
  useEffect(() => {
    // 1. Pehle 'Deals' aur 'Meals' dono ko main grid se nikal dein 
    // taake wo "All" mein niche nazar na aayein
    let temp = products.filter(p => {
      const cat = (p.category?.name || p.category);
      return cat !== 'Deals' && cat !== 'Meals';
    });

    // 2. Agar user ne specifically koi category select ki hai (e.g. 'Pizzas')
    // Note: Agar aap chahte hain ke 'Meals' tab par click karne se niche bhi dikhein, 
    // to niche wali logic usay handle karegi.
    if (activeCategory !== 'All') {
      // Jab user specific category select karega, to hum pure products se filter karenge
      temp = products.filter(p => (p.category?.name || p.category) === activeCategory);
    }

    // 3. Search filter lagayein
    if (searchQuery) {
      temp = temp.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredProducts(temp);
  }, [searchQuery, activeCategory, products]);

  // --- CART FUNCTIONS UPDATED FOR VARIANTS ---
  const addToCart = (product, variant = null) => {
    setCart((prev) => {
      const cartId = variant ? `${product._id}-${variant.name}` : product._id;

      const exists = prev.find(item => item.cartId === cartId);
      if (exists) {
        return prev.map(item => item.cartId === cartId ? { ...item, qty: item.qty + 1 } : item);
      }

      return [...prev, {
        ...product,
        cartId,
        qty: 1,
        selectedVariant: variant,
        price: variant ? variant.price : product.price
      }];
    });
    toast.success(`${product.name}${variant ? ` (${variant.name})` : ''} added`);
  };

  // --- UPDATED updateQty ---
  const updateQty = (cartId, delta) => {
    setCart(prev => {
      const existingItem = prev.find(item => item.cartId === cartId);

      // Agar 1 quantity pe minus dabaye to remove karega
      if (existingItem && existingItem.qty === 1 && delta === -1) {
        // toast.error("Removed  cart"); 
        return prev.filter(item => item.cartId !== cartId);
      }

      return prev.map(item => {
        if (item.cartId === cartId) {
          const newQty = item.qty + delta;
          return newQty > 0 ? { ...item, qty: newQty } : item;
        }
        return item;
      });
    });
  };

  // --- UPDATED removeFromCart (Notifications yahan se hata di taake clash na ho) ---
  const removeFromCart = (cartId) => {
    setCart(prev => {
      const exists = prev.find(item => item.cartId === cartId);
      // if(exists) toast.error("Removed from cart"); // Notification sirf ek baar ayeg
      return prev.filter(item => item.cartId !== cartId);
    });
  };

  const totalPrice = cart.reduce((total, item) => total + (item.price * item.qty), 0);
  // --- CART FUNCTIONS END ---

  // --- ORDER PROCESS & GEOLOCATION FIX START ---
  const handleLocationChoice = async (shouldShare) => {
    const rawNumber = seller?.whatsapp || "923000000000";
    const cleanPhone = rawNumber.replace(/\D/g, '');

    if (shouldShare && "geolocation" in navigator) {
      setLocationLoading(true);

      const getPosition = () => {
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, // Is se browser system GPS ko trigger karne ki koshish karta hai
            timeout: 10000,
            maximumAge: 0
          });
        });
      };

      try {
        const position = await getPosition();
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        // Google Maps ka sahi format jo WhatsApp par preview dikhata hai
        const mapsLink = `https://www.google.com/maps?q=${lat},${lng}`;

        finishOrder(cleanPhone, mapsLink);

      } catch (error) {
        console.error("Location Error:", error);

        if (error.code === 1) {
          // User ne browser permission block ki hui hai
          toast.error("Please allow location permission in browser settings.");
        } else if (error.code === 2 || error.code === 3) {
          // Device ka GPS band hai ya signal nahi mil raha
          // Yahan hum alert dikhayenge jo user ko settings mein jane par majboor karega
          alert("Location access is required to share your location. Please enable GPS and try again.");
        } else {
          toast.error("Unable to get location. Please share your location manually on WhatsApp.");
          finishOrder(cleanPhone, null);
        }
      } finally {
        setLocationLoading(false);
      }
    } else {
      finishOrder(cleanPhone, null);
    }
  };



  const finishOrder = async (phone, mapsLink) => {
    const orderData = {
      items: cart.map(item => ({
        productId: item._id,
        name: item.name,
        qty: item.qty,
        price: item.price,
        variant: item.selectedVariant ? item.selectedVariant.name : null
      })),
      totalAmount: totalPrice,
      shopId: seller._id,
      location: mapsLink || "Shared manually"
    };

    try {
      await api.post('/orders', orderData);

      let itemsList = cart.map((item, i) => {
        const variantName = item.selectedVariant ? ` (${item.selectedVariant.name})` : '';
        return `${i + 1}. *${item.name}${variantName}* (x${item.qty}) - Rs.${item.price * item.qty}`;
      }).join('%0A');

      // Agar link hai to Maps link jaye, warna simple text
      const locationText = mapsLink
        ? `%0A📍 *Location:* ${mapsLink}`
        : `%0A📍 *Location:* (Shared manually on WhatsApp)`;

      const message = `Assalam-o-Alaikum!%0A%0AI want to order from *${seller?.shopName || shopName}*:%0A%0A${itemsList}%0A%0A*Total:* Rs. ${totalPrice}${locationText}`;

      // Order hone ke baad states reset karein
      setCart([]);
      setIsLocationModalOpen(false);
      setLocationLoading(false);

      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${message}`, '_blank');
    } catch (err) {
      setLocationLoading(false);
      toast.error("Failed to place order. Please try again.");
    }
  };
  // --- ORDER PROCESS & GEOLOCATION FIX END ---

  if (loading) return (
    <div className="flex flex-col h-screen items-center justify-center gap-4 bg-white">
      <Loader2 className="animate-spin text-green-600" size={40} />
      <p className="font-bold text-gray-500 italic uppercase text-xs tracking-widest text-center px-4">Loading...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-40">

      {/* --- HEADER SECTION --- */}
      <div className="relative bg-white border-b rounded-b-[3.5rem] shadow-sm overflow-hidden z-10">
        <div className="h-44 sm:h-60 w-full bg-gray-100 relative">
          {seller?.coverPhoto ? (
            <img src={seller.coverPhoto} alt="Store Cover" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-green-50 to-green-100" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        </div>

        <div className="relative px-6 pb-10 text-center -mt-12 z-20">
          <div className="inline-block relative">
            {seller?.shopLogo ? (
              <img src={seller.shopLogo} className="w-24 h-24 rounded-full border-4 border-white shadow-xl object-cover bg-white" alt="logo" />
            ) : (
              <div className="w-24 h-24 rounded-full border-4 border-white shadow-xl bg-gray-50 flex items-center justify-center">
                <span className="text-gray-300 font-bold">No Logo</span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900 capitalize tracking-tight">
              {seller?.name || shopName.replace('-', ' ')}
            </h1>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-50 rounded-full mt-3">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <p className="text-[10px] sm:text-xs text-green-700 font-black uppercase tracking-widest">
                Order directly on WhatsApp
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- DEALS SLIDER --- */}
      {deals.length > 0 && (
        <DealsSlider
          deals={deals}
          onSeeMore={setSelectedProduct}
          cart={cart}
          onAdd={addToCart}
          onUpdateQty={updateQty}
          onRemove={removeFromCart}
        />
      )}

      <MealsSlider
        products={products}
        onAdd={addToCart}
        onUpdateQty={updateQty}
        onRemove={removeFromCart}
        cart={cart}
        onSeeMore={setSelectedProduct}
      />

      {/* --- NAVIGATION & SEARCH --- */}
      <div className="sticky top-0 z-[100] bg-gray-50/80 backdrop-blur-lg pb-4 border-b border-gray-100">
        <div className="flex gap-3 overflow-x-auto p-4 no-scrollbar items-center justify-start sm:justify-center">
          {/* FIX: Categories listing for 'All' items excluding deals */}
          {['All', ...new Set(products.filter(p => (p.category?.name || p.category) !== 'Deals' && (p.category?.name || p.category) !== 'Meals').map(p => p.category?.name || p.category))].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 whitespace-nowrap w-max flex-shrink-0 shadow-sm border ${activeCategory === cat
                ? "bg-black text-white border-black shadow-lg scale-105"
                : "bg-white text-gray-400 border-gray-100 hover:border-gray-300"
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="px-4 max-w-xl mx-auto flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-4 bg-white rounded-2xl text-sm font-bold shadow-sm border border-transparent focus:border-green-500 outline-none transition-all"
            />
          </div>
          <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-gray-100">
            <button onClick={() => setViewMode('list')} className={`p-3 rounded-xl transition-all ${viewMode === 'list' ? 'bg-black text-white shadow-md' : 'text-gray-400'}`}><List size={20} /></button>
            <button onClick={() => setViewMode('grid')} className={`p-3 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-black text-white shadow-md' : 'text-gray-400'}`}><LayoutGrid size={20} /></button>
          </div>
        </div>
      </div>

      {/* --- PRODUCT GRID --- */}

<div className={`px-4 grid gap-4 sm:gap-6 mt-8 ${viewMode === 'grid' ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
  {filteredProducts.length > 0 ? (
    filteredProducts.map(product => (
      <LazyLoad 
        key={product._id} 
        height={viewMode === 'grid' ? 250 : 150} // Grid mode mein card lamba hota hai, list mein chota
        offset={100}       // Screen se 100px pehle load shuru karega
        once={true}        // Performance ke liye behtar hai ke ek baar load ho jaye to baar baar check na kare
        placeholder={
          <div className={`bg-gray-100 animate-pulse rounded-2xl ${viewMode === 'grid' ? "h-[250px]" : "h-[120px]"} w-full`} />
        }
      >
        <ProductCard 
          product={product} 
          viewMode={viewMode} 
          onAdd={addToCart} 
          onUpdateQty={updateQty} 
          onRemove={removeFromCart} 
          cart={cart} 
          onSeeMore={() => setSelectedProduct(product)} 
        />
      </LazyLoad>
    ))
  ) : (
    <div className="col-span-full py-20 text-center">
      <p className="text-gray-400 font-bold italic">No items found...</p>
    </div>
  )}
</div>
      {/* --- MODALS --- */}
      {selectedProduct && <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} onAdd={addToCart} />}
      {isLocationModalOpen && <LocationModal loading={locationLoading} onChoice={handleLocationChoice} />}

      {/* --- BOTTOM CART BAR --- */}
      {cart.length > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] w-[92%] max-w-md animate-in slide-in-from-bottom-10">
          <div className="bg-black text-white p-2 pl-6 rounded-[2.5rem] shadow-2xl flex items-center justify-between border border-white/10 backdrop-blur-md">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Total Amount</span>
              <span className="text-xl font-black text-green-400 leading-none">Rs. {totalPrice}</span>
            </div>
            <button onClick={() => setIsLocationModalOpen(true)} className="bg-green-500 text-black px-10 py-5 rounded-[2rem] font-black flex items-center gap-2 active:scale-95 transition-all">
              Checkout <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- DEALS SLIDER COMPONENT ---
const DealsSlider = ({ deals, onSeeMore, cart, onAdd, onUpdateQty, onRemove }) => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    if (deals.length <= 1 || isPaused) return;
    const interval = setInterval(() => setCurrent((prev) => (prev + 1) % deals.length), 5000);
    return () => clearInterval(interval);
  }, [deals.length, isPaused]);

  const handleTouchStart = (e) => { setIsPaused(true); setTouchStart(e.targetTouches[0].clientX); };
  const handleTouchEnd = () => {
    setIsPaused(false);
    const distance = touchStart - touchEnd;
    if (distance > 70) setCurrent((prev) => (prev + 1) % deals.length);
    if (distance < -70) setCurrent((prev) => (prev === 0 ? deals.length - 1 : prev - 1));
    setTouchEnd(0);
  };

  const stopSliderAction = (e) => e.stopPropagation();

  return (
    <div className="px-4 mt-4 mb-2 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-3 px-1">
        <span className="flex h-2 w-2 rounded-full bg-red-500 animate-ping"></span>
        <h2 className="text-lg font-black text-gray-900 uppercase tracking-tighter">Hot Deals 🔥</h2>
      </div>

      <div
        className="relative h-[200px] sm:h-[350px] w-full overflow-hidden rounded-[2.5rem] sm:rounded-[3.5rem] shadow-xl border border-gray-100 bg-black group touch-pan-y"
        onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart} onTouchMove={(e) => setTouchEnd(e.targetTouches[0].clientX)} onTouchEnd={handleTouchEnd}
      >
        {deals.map((deal, i) => {
          const cartItem = cart.find(item => item._id === deal._id);
          return (
            <div key={deal._id} className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${i === current ? "opacity-100 translate-x-0 scale-100" : i < current ? "opacity-0 -translate-x-full scale-95" : "opacity-0 translate-x-full scale-95"}`}>
              <img src={deal.images?.[0] || deal.image} className="w-full h-full object-contain bg-black pointer-events-none" alt={deal.name} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />

              <div className="absolute bottom-6 left-4 right-4 sm:bottom-8 sm:left-10 sm:right-10 flex items-center justify-between z-20">
                <button onClick={(e) => { e.stopPropagation(); onSeeMore(deal); }} onTouchStart={(e) => e.stopPropagation()} className="bg-white/95 backdrop-blur-md text-black px-4 py-2.5 sm:px-8 sm:py-4 rounded-xl sm:rounded-[2rem] font-black text-[10px] sm:text-xs flex items-center gap-2 shadow-lg active:scale-95">
                  Details <ChevronRight size={14} />
                </button>
                <div className="flex items-center gap-2" onClick={stopSliderAction} onTouchStart={stopSliderAction} onTouchMove={stopSliderAction} onTouchEnd={stopSliderAction}>
                  {cartItem ? (
                    <div className="flex items-center bg-white/95 backdrop-blur-md rounded-xl sm:rounded-[2rem] p-1 shadow-2xl">
                      <button onClick={(e) => { e.stopPropagation(); onRemove(deal._id); }} className="p-2 bg-red-50 text-red-500 rounded-lg"><Trash2 size={14} /></button>
                      <div className="h-6 w-[1px] bg-gray-200 mx-1" />
                      <button onClick={(e) => { e.stopPropagation(); onUpdateQty(deal._id, -1); }} className="p-2 bg-gray-100 rounded-lg"><Minus size={14} /></button>
                      <span className="font-black text-xs px-1 min-w-[20px] text-center text-black">{cartItem.qty}</span>
                      <button onClick={(e) => { e.stopPropagation(); onUpdateQty(deal._id, 1); }} className="p-2 bg-green-500 rounded-lg text-white"><Plus size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); onAdd(deal); }} className="p-3.5 sm:p-5 bg-green-500 text-white rounded-xl sm:rounded-[2rem] shadow-lg active:scale-90"><Plus size={20} strokeWidth={4} /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* --- DYNAMIC SLIDER DOTS --- */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
          {deals.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};


const MealsSlider = ({ products, onAdd, onUpdateQty, onRemove, cart, onSeeMore }) => {
  // Sirf 'Meals' filter karein
  const mealItems = products.filter(p => (p.category?.name || p.category) === 'Meals');

  if (mealItems.length === 0) return null;

  return (
    <div className="py-8 bg-gray-50/50">
      <div className="px-6 mb-6">
        <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter flex items-center gap-2">
          <Package className="text-red-600" size={20} /> Best Meals
        </h2>
        <div className="h-1 w-10 bg-red-600 rounded-full mt-1"></div>
      </div>

      <div className="flex gap-5 overflow-x-auto no-scrollbar px-6 pb-6">
        {mealItems.map((item) => {
          // Check karein ke item cart mein hai ya nahi
          const cartItem = cart?.find(i => i.cartId === item._id);

          return (
            <div
              key={item._id}
              className="min-w-[220px] bg-white rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col relative overflow-hidden group"
            >
              {/* Price Tag - Overlapping Style */}
              <div className="absolute top-4 right-0 z-20">
                <div className="bg-red-600 text-white pl-4 pr-3 py-1.5 font-black text-xs rounded-l-2xl shadow-md">
                  Rs. {item.price}
                </div>
              </div>

              {/* Image Section */}
              <div
                className="h-40 w-full p-4 cursor-pointer"
                onClick={() => onSeeMore(item)}
              >
                <img
                  src={item.images?.[0] || item.image}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  alt={item.name}
                />
              </div>

              {/* Content Section */}
              <div className="p-5 pt-0 flex flex-col flex-1">
                <h3 className="font-black text-gray-900 text-sm truncate mb-4">
                  {item.name}
                </h3>

                <div className="mt-auto">
                  {cartItem ? (
                    /* Quantity Controls - Aapke style mein */
                    <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-1 border border-gray-100">
                      <button
                        onClick={() => onUpdateQty(item._id, -1)}
                        className="p-2 bg-white rounded-xl text-black shadow-sm active:scale-90"
                      >
                        <Minus size={14} />
                      </button>

                      <span className="font-black text-sm">{cartItem.qty}</span>

                      <button
                        onClick={() => onUpdateQty(item._id, 1)}
                        className="p-2 bg-red-600 text-white rounded-xl shadow-sm active:scale-90"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  ) : (
                    /* Add to Cart Button - Aapke style mein */
                    <button
                      onClick={() => onAdd(item)}
                      className="w-full py-3 bg-black text-white rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition-all shadow-lg"
                    >
                      <Plus size={14} strokeWidth={3} /> Add to Cart
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- PRODUCT CARD COMPONENT ---
const ProductCard = ({ product, onAdd, onUpdateQty, onRemove, cart, onSeeMore, viewMode }) => {
  const isGrid = viewMode === 'grid';
  const hasVariants = product.variants && product.variants.length > 0;
  const [activeVar, setActiveVar] = useState(hasVariants ? product.variants[0] : null);

  // --- IMAGE OPTIMIZATION LOGIC ---
  const rawImg = product.images?.length > 0 ? product.images[0] : product.image;

  const getOptimizedUrl = (url) => {
    if (!url) return "";
    
    // Agar Cloudinary URL hai aur transformations missing hain
    if (url.includes("cloudinary.com") && !url.includes("/upload/f_auto")) {
      // f_auto: Format auto (WebP), q_auto: Quality auto, w_500: Width limit
      return url.replace("/upload/", "/upload/f_auto,q_auto,w_500,c_limit/");
    }
    return url;
  };

  const mainImg = getOptimizedUrl(rawImg);

  const currentCartId = activeVar ? `${product._id}-${activeVar.name}` : product._id;
  const cartItem = cart?.find(item => item.cartId === currentCartId);

  return (
    <div className={`bg-white rounded-[2rem] overflow-hidden border border-gray-100 flex flex-col shadow-sm group relative transition-all ${isGrid ? "rounded-[1.5rem]" : "rounded-[2.5rem]"}`}>
      {cartItem && (
        <button 
          onClick={(e) => { e.stopPropagation(); onRemove(currentCartId); }} 
          className="absolute top-3 right-3 z-30 p-1.5 bg-red-500 text-white rounded-full shadow-lg active:scale-75"
        >
          <X size={14} strokeWidth={3} />
        </button>
      )}
      
      <div className={`relative bg-gray-50 overflow-hidden cursor-pointer ${isGrid ? "h-40" : "h-64"}`} onClick={onSeeMore}>
        {/* Optimized image with lazy loading */}
        <img 
          src={mainImg} 
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
          alt={product.name} 
          loading="lazy" 
        />

        <div className={`absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2 py-0.5 rounded-full font-black uppercase shadow-sm ${isGrid ? "text-[7px]" : "text-[9px]"}`}>
          {product.category?.name || product.category}
        </div>
      </div>

      <div className={`${isGrid ? "p-3" : "p-6"} flex flex-col flex-1`}>
        <h3 className={`font-black text-gray-900 truncate tracking-tight ${isGrid ? "text-sm" : "text-xl"}`}>{product.name}</h3>

        {hasVariants && (
          <div className="flex flex-wrap gap-1 mt-2 mb-1">
            {product.variants.map((v, i) => (
              <button 
                key={i} 
                onClick={(e) => { e.stopPropagation(); setActiveVar(v); }} 
                className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase border transition-all ${activeVar?.name === v.name ? "bg-black text-white border-black" : "bg-gray-50 text-gray-400 border-gray-100"}`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}

        {!isGrid && (
          <div className="mt-2 flex-1">
            <div className="text-gray-400 text-xs line-clamp-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: product.description }} />
            <button onClick={onSeeMore} className="text-green-600 text-[10px] font-black uppercase mt-2 flex items-center gap-1">
              Details <ChevronRight size={12} />
            </button>
          </div>
        )}

        <div className={`flex items-center justify-between ${isGrid ? "mt-3" : "mt-6"}`}>
          <div className="flex flex-col">
            <span className={`${isGrid ? "text-[8px]" : "text-xs"} text-gray-400 font-bold uppercase`}>Price</span>
            <span className={`font-black text-gray-900 font-mono ${isGrid ? "text-sm" : "text-2xl"}`}>
              Rs.{activeVar ? activeVar.price : product.price}
            </span>
          </div>
          
          {cartItem ? (
            <div className={`flex items-center bg-gray-100 rounded-xl border border-gray-200/50 ${isGrid ? "p-1 gap-1" : "p-1.5 gap-3"}`}>
              <button onClick={(e) => { e.stopPropagation(); onUpdateQty(currentCartId, -1); }} className={`${isGrid ? "p-1" : "p-2.5"} bg-white rounded-lg text-black hover:bg-gray-50`}>
                <Minus size={isGrid ? 12 : 16} />
              </button>
              <span className={`font-black ${isGrid ? "text-xs min-w-[12px]" : "text-lg min-w-[20px]"} text-center`}>{cartItem.qty}</span>
              <button onClick={(e) => { e.stopPropagation(); onUpdateQty(currentCartId, 1); }} className={`${isGrid ? "p-1" : "p-2.5"} bg-green-500 text-white rounded-lg hover:bg-green-600`}>
                <Plus size={isGrid ? 12 : 16} />
              </button>
            </div>
          ) : (
            <button 
              onClick={(e) => { e.stopPropagation(); onAdd(product, activeVar); }} 
              className={`${isGrid ? "p-2.5" : "p-4"} bg-black text-white rounded-xl shadow-xl active:scale-90 hover:bg-gray-800 transition-colors`}
            >
              <Plus size={isGrid ? 18 : 24} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- PRODUCT MODAL ---
const ProductModal = ({ product, onClose, onAdd }) => {
  // Logic: Agar 2 images hain to index 1 wali dikhaye, warna index 0
  const modalImage = product.images?.length > 1 ? product.images[1] : (product.images?.[0] || product.image);

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-xl z-[900] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in">
      <button onClick={onClose} className="fixed top-6 right-6 z-[999] p-4 bg-white text-black rounded-full shadow-2xl active:scale-90"><X size={28} strokeWidth={3} /></button>

      <div className="bg-white rounded-t-[3.5rem] sm:rounded-[3rem] w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden relative animate-in slide-in-from-bottom-20">
        <div className="overflow-y-auto no-scrollbar pb-32">
          {/* Square Image Box: Mobile aur Desktop dono ke liye optimize */}
          <div className="w-full aspect-square bg-white flex items-center justify-center overflow-hidden border-b border-gray-50">
            <img
              src={modalImage}
              className="w-full h-full object-contain p-4"
              alt="product detail"
            />
          </div>

          <div className="p-8">
            <span className="text-[10px] font-black text-green-600 uppercase tracking-[0.3em]">{product.category?.name || product.category}</span>
            <h3 className="font-black text-3xl text-gray-900 mt-2 leading-tight">{product.name}</h3>

            <div className="mt-6 space-y-4">
              <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Description</p>
              <div className="prose prose-sm max-w-none text-gray-600 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-8 border-t bg-white/95 backdrop-blur-xl flex justify-between items-center gap-6 z-[910]">
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-400 font-bold uppercase">Price</span>
            <span className="font-black text-3xl text-gray-900">Rs. {product.price}</span>
          </div>
          <button onClick={() => { onAdd(product); onClose(); }} className="flex-1 py-5 bg-black text-white rounded-[2rem] font-black text-lg flex items-center justify-center gap-3 shadow-2xl">
            <ShoppingCart size={22} className="text-green-400" /> Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

// --- LOCATION MODAL ---
const LocationModal = ({ loading, onChoice }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[600] flex items-center justify-center p-6">
    <div className="bg-white rounded-[3rem] w-full max-w-xs p-10 text-center animate-in zoom-in">
      <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6"><MapPin className="text-green-600" size={36} /></div>
      <h3 className="text-2xl font-black mb-2 tracking-tight">Location</h3>
      <p className="text-gray-500 text-sm mb-8 leading-relaxed">Please enable location access to share your location. For delivery purposes.</p>
      <div className="flex flex-col gap-3">
        <button onClick={() => onChoice(true)} disabled={loading} className="w-full py-4 bg-green-600 text-white rounded-2xl font-black shadow-lg flex items-center justify-center min-h-[56px]">{loading ? <Loader2 className="animate-spin" size={24} /> : "Yes, Share Link"}</button>
        <button onClick={() => onChoice(false)} disabled={loading} className="w-full py-4 bg-gray-100 text-gray-500 rounded-2xl font-black uppercase text-[10px] tracking-widest">Skip</button>
      </div>
    </div>
  </div>
);

export default StoreView;